import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES } from "@/data/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, X, Save, RotateCcw } from "lucide-react";
import CategoryIcon from "@/components/CategoryIcon";

export default function AdminTemplates() {
  const [active, setActive] = useState(CATEGORIES[0].slug);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900">Grading templates</h1>
      <p className="mt-3 text-gray-700 max-w-2xl">Each category has its own inspection framework. Edit sections and items — inspectors will grade against these on their next visit. Use “Restore default template” to roll back any tweaks to the shipped framework for that category only.</p>

      <Tabs value={active} onValueChange={setActive} className="mt-8">
        <TabsList className="flex flex-wrap h-auto bg-gray-100 p-1 rounded-xl">
          {CATEGORIES.map(c => (
            <TabsTrigger key={c.slug} value={c.slug} data-testid={`tab-${c.slug}`}
              className="data-[state=active]:bg-gray-900 data-[state=active]:text-white rounded-lg px-3 py-2">
              <span className="inline-flex items-center gap-2"><CategoryIcon slug={c.slug} className="w-4 h-4"/>{c.short}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {CATEGORIES.map(c => (
          <TabsContent key={c.slug} value={c.slug} className="mt-6">
            <TemplateEditor category={c.slug} categoryName={c.name} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function TemplateEditor({ category, categoryName }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/inspection-templates/${category}`)
      .then(r => setSections(r.data.sections || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [category]);

  const addSection = () => setSections([...sections, { section: "New section", items: [] }]);
  const removeSection = (idx) => setSections(sections.filter((_, i) => i !== idx));
  const updateSection = (idx, patch) => setSections(sections.map((s, i) => i !== idx ? s : { ...s, ...patch }));
  const addItem = (sIdx) => updateSection(sIdx, { items: [...sections[sIdx].items, "New criterion"] });
  const removeItem = (sIdx, iIdx) => updateSection(sIdx, { items: sections[sIdx].items.filter((_, i) => i !== iIdx) });
  const updateItem = (sIdx, iIdx, value) =>
    updateSection(sIdx, { items: sections[sIdx].items.map((it, i) => i !== iIdx ? it : value) });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/inspection-templates/${category}`, {
        sections: sections.map(s => ({ section: s.section, items: s.items.filter(Boolean) })),
      });
      toast.success(`${categoryName} template saved`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const restoreDefault = async () => {
    setResetting(true);
    try {
      const r = await api.post(`/inspection-templates/${category}/reset`);
      setSections(r.data.sections || []);
      toast.success(`${categoryName} restored to default template`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    } finally { setResetting(false); }
  };

  if (loading) return <div className="text-gray-500">Loading template…</div>;

  return (
    <div className="space-y-4">
      {sections.map((sec, sIdx) => (
        <Card key={sIdx} className="border border-gray-200 rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <Input value={sec.section} onChange={e => updateSection(sIdx, { section: e.target.value })}
                className="font-heading text-lg font-semibold border-2 border-gray-300 focus:border-blue-600 h-11" data-testid={`section-name-${sIdx}`}/>
              <Button variant="ghost" onClick={() => removeSection(sIdx)} aria-label="Remove section" data-testid={`remove-section-${sIdx}`}><X className="w-4 h-4"/></Button>
            </div>
            <div className="mt-3 space-y-2">
              {sec.items.map((it, iIdx) => (
                <div key={iIdx} className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-6 text-right">{iIdx + 1}.</span>
                  <Input value={it} onChange={e => updateItem(sIdx, iIdx, e.target.value)}
                    className="border-2 border-gray-200 focus:border-blue-600" data-testid={`item-${sIdx}-${iIdx}`}/>
                  <Button variant="ghost" size="icon" onClick={() => removeItem(sIdx, iIdx)} aria-label="Remove item"><X className="w-4 h-4"/></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addItem(sIdx)} className="rounded-full" data-testid={`add-item-${sIdx}`}><Plus className="w-4 h-4 mr-1"/>Add item</Button>
            </div>
          </CardContent>
        </Card>
      ))}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={addSection} className="rounded-full border-2 border-gray-900" data-testid="add-section-btn"><Plus className="w-4 h-4 mr-1"/>Add section</Button>
        <Button onClick={save} disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="save-template-btn"><Save className="w-4 h-4 mr-1"/>{busy ? "Saving…" : "Save template"}</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={resetting} className="rounded-full border-2 border-amber-600 text-amber-700 hover:bg-amber-50" data-testid={`restore-default-btn-${category}`}>
              <RotateCcw className="w-4 h-4 mr-1"/>{resetting ? "Restoring…" : "Restore default template"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Restore default template?</AlertDialogTitle>
              <AlertDialogDescription>
                This will replace the current sections and items for <b>{categoryName}</b> with the default Access Local framework. Any custom edits for this category will be lost. Other categories are unaffected.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`restore-cancel-${category}`}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={restoreDefault} className="bg-amber-600 hover:bg-amber-700" data-testid={`restore-confirm-${category}`}>
                Restore
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
